import {PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {differenceInMilliseconds} from 'date-fns';
import {Box, Show} from 'platform/foundation';

import {useState} from 'react';

import {parseDate, pushGtmEvent, suffixTestId} from 'shared/web';

import {BigCalendarDndContext} from './components/BigCalendarDndContext';
import {DayView} from './components/DayView';
import {Header} from './components/Header';
import {MonthView} from './components/MonthView';
import {WeekView} from './components/WeekView';
import {useOptimisticEventManager} from './hooks/useOptimisticEventManager';
import {dragStore} from './store/dragStore';
import type {BigCalendarEvent} from './types/BigCalendarEvent';
import type {BigCalendarProps} from './types/BigCalendarProps';
import type {BigCalendarViewType} from './types/BigCalendarViewType';
import {calculedResizedEventToBottom} from './utils/calculedResizedEventToBottom';

/**
 * A powerful and flexible calendar component that provides comprehensive event management capabilities
 * with support for day, week, month, and resource views. Built with drag-and-drop functionality and optimistic
 * updates for a smooth user experience.
 *
 * @component
 * @param {BigCalendarProps} props - The component configuration props
 * @param {BigCalendarEvent[]} props.events - Array of calendar events to display in the calendar
 * @param {BigCalendarViewType} [props.defaultView='week'] - Initial calendar view type ('day', 'week', 'month', or 'resource')
 * @param {BigCalendarViewType[]} [props.allowedViews=['week', 'month', 'day']] - Array of allowed view types. When only one view is provided, view selector is hidden
 * @param {boolean} [props.isReadOnly=false] - When true, disables drag-and-drop and editing functionality
 * @param {Date} [props.currentDate] - The date to display in the calendar view
 * @param {User} [props.currentUser] - Currently logged in user information
 * @param {User[]} [props.selectedUsers] - Array of selected users for filtering events
 * @param {User[]} [props.users] - Array of all available users
 * @param {(event: BigCalendarEvent) => void} [props.onEventClick] - Handler called when an event is clicked
 * @param {(date: Date) => void} [props.onDateClick] - Handler called when a date cell is clicked
 * @param {(event: BigCalendarEvent, start: Date, end: Date) => void} [props.onEventDragMove] - Handler called when an event is dragged to a new position
 * @param {(date: Date) => void} [props.onNavigate] - Handler called when calendar navigation occurs
 * @param {(changes: UserControlChanges) => void} [props.onUserControlChange] - Handler called when user selection changes
 * @param {boolean} [props.enableHover=false] - When true, enables hover effects on calendar events (darkens color on hover)
 * @param {(props: BigCalendarPopupProps) => ReactNode} [props.renderEventPopup] - Function to render custom popup when hovering over events
 *
 * @example
 * Basic usage with minimal props:
 * ```tsx
 * <BigCalendar
 *   events={myEvents}
 *   defaultView="week"
 *   currentDate={new Date()}
 *   onEventClick={(event) => handleEventClick(event)}
 * />
 * ```
 *
 * @example
 * Advanced usage with all features enabled:
 * ```tsx
 * <BigCalendar
 *   events={myEvents}
 *   defaultView="week"
 *   allowedViews={['week', 'month']} // Only week and month views are available
 *   currentDate={new Date()}
 *   currentUser={currentUser}
 *   selectedUsers={selectedUsers}
 *   users={allUsers}
 *   isReadOnly={false}
 *   onEventClick={(event) => handleEventClick(event)}
 *   onDateClick={(date) => handleDateClick(date)}
 *   onEventDragMove={async (event, start, end) => {
 *     try {
 *       await updateEventTimes(event.id, start, end);
 *       showSuccessNotification();
 *     } catch (error) {
 *       showErrorNotification();
 *     }
 *   }}
 *   onNavigate={(date) => updateUrlWithNewDate(date)}
 *   onUserControlChange={(changes) => handleUserFiltering(changes)}
 * />
 * ```
 *
 * @example
 * Calendar with only one view (view selector hidden):
 * ```tsx
 * <BigCalendar
 *   events={myEvents}
 *   defaultView="month"
 *   allowedViews={['month']} // Only month view, selector will be hidden
 *   currentDate={new Date()}
 *   onEventClick={(event) => handleEventClick(event)}
 *   onNavigate={(date) => updateUrlWithNewDate(date)}
 * />
 * ```
 *
 * @example
 * Calendar with hover effects enabled:
 * ```tsx
 * <BigCalendar
 *   events={myEvents}
 *   defaultView="week"
 *   currentDate={new Date()}
 *   currentUser={currentUser}
 *   onEventClick={(event) => handleEventClick(event)}
 *   enableHover={true} // Events will darken on hover
 * />
 * ```
 *
 * @example
 * Calendar with custom event popup:
 * ```tsx
 * <BigCalendar
 *   events={myEvents}
 *   defaultView="week"
 *   currentDate={new Date()}
 *   onEventClick={(event) => handleEventClick(event)}
 *   renderEventPopup={({event, position, isVisible}) => (
 *     <div
 *       style={{
 *         position: 'fixed',
 *         left: position.x,
 *         top: position.y,
 *         background: 'white',
 *         border: '1px solid #ccc',
 *         padding: '8px',
 *         borderRadius: '4px',
 *         boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
 *         pointerEvents: 'none',
 *         zIndex: 1000,
 *       }}
 *     >
 *       <div>{event.title}</div>
 *       <div>Start: {event.start.toLocaleString()}</div>
 *       <div>End: {event.end.toLocaleString()}</div>
 *     </div>
 *   )}
 * />
 * ```
 *
 * Key Features:
 * - Multiple calendar views (day, week, month, resource) with easy switching
 * - Drag-and-drop event management with optimistic updates
 * - User filtering and multi-user support
 * - Responsive design that works across device sizes
 * - Read-only mode for view-only scenarios
 * - Customizable event rendering
 * - Custom event popups on hover
 * - Accessibility support
 * - Timezone aware
 *
 * Technical Details:
 * - Uses dnd-kit for smooth drag-and-drop functionality
 * - Implements optimistic updates for immediate UI feedback
 * - Maintains internal state for view type, current date, and drag operations
 * - Supports both all-day and timed events
 * - Handles event overlap and collision detection
 * - Provides granular control over event interactions
 *
 * @see {@link https://github.com/clauderic/dnd-kit} - dnd-kit documentation
 * @see {@link BigCalendarEvent} - Event object structure
 * @see {@link BigCalendarViewType} - Available view types
 */
export function BigCalendar(props: BigCalendarProps) {
  const {optimisticEvents, optimisticEventUpdate} = useOptimisticEventManager(props.events);

  const [view, setView] = useState<BigCalendarViewType>(props.defaultView || 'week');

  // Popup state
  const [hoveredEvent, setHoveredEvent] = useState<BigCalendarEvent | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    dragStore.startDragging(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.over || props.isReadOnly || !props.onEventDragMove) {
      dragStore.resetDragState();
      return;
    }

    const dndEventData = event.active?.data?.current;

    if (dndEventData?.type === 'resize' && dndEventData?.direction === 'bottom') {
      const originalEvent = optimisticEvents.find((event) => event.id === dndEventData?.eventId);

      if (!originalEvent) {
        dragStore.resetDragState();
        return;
      }

      const endDate = calculedResizedEventToBottom({
        start: originalEvent.start,
        end: originalEvent.end,
        overId: event.over.id,
      });

      if (!endDate) {
        dragStore.resetDragState();
        return;
      }

      pushGtmEvent({
        event: 'drag_and_drop',
        event_id: 'big_calendar_event_resize',
        new_value: {
          eventId: originalEvent.id,
          start: originalEvent.start.toISOString(),
          end: endDate.toISOString(),
        },
        previous_value: {
          eventId: originalEvent.id,
          start: originalEvent.start.toISOString(),
          end: originalEvent.end.toISOString(),
        },
      });

      optimisticEventUpdate({
        event: originalEvent,
        start: originalEvent.start,
        end: endDate,
        onEventDragMove: props.onEventDragMove,
      });
    }

    const dropTimestamp = parseInt(String(event.over.id), 10);
    const newStart = parseDate(dropTimestamp);
    const originalEvent = optimisticEvents.find((e) => e.id === event.active.id);

    if (!originalEvent) {
      dragStore.resetDragState();
      return;
    }

    if (originalEvent.isAllDay) {
      pushGtmEvent({
        event: 'drag_and_drop',
        event_id: 'big_calendar_all_day_event_drag_move',
        new_value: {
          eventId: originalEvent.id,
          start: newStart.toISOString(),
          end: newStart.toISOString(),
        },
        previous_value: {
          eventId: originalEvent.id,
          start: originalEvent.start.toISOString(),
          end: originalEvent.end.toISOString(),
        },
      });

      optimisticEventUpdate({
        event: originalEvent,
        start: newStart,
        end: newStart,
        onEventDragMove: props.onEventDragMove,
      });
      return;
    }

    const duration = differenceInMilliseconds(originalEvent.end, originalEvent.start);
    const newEnd = parseDate(newStart.getTime() + duration);

    pushGtmEvent({
      event: 'drag_and_drop',
      event_id: 'big_calendar_event_drag_move',
      new_value: {
        eventId: originalEvent.id,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
      },
      previous_value: {
        eventId: originalEvent.id,
        start: originalEvent.start.toISOString(),
        end: originalEvent.end.toISOString(),
      },
    });

    optimisticEventUpdate({
      event: originalEvent,
      start: newStart,
      end: newEnd,
      onEventDragMove: props.onEventDragMove,
    });
  };

  const handleEventMouseEnter = (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => {
    if (!props.renderEventPopup) {
      return;
    }

    setAnchorElement(mouseEvent.currentTarget as HTMLElement);
    setHoveredEvent(event);
  };

  const handleEventMouseLeave = () => {
    setHoveredEvent(null);
    setAnchorElement(null);
  };

  return (
    <BigCalendarDndContext
      data-testid={suffixTestId('bigCalendar', props)}
      sensors={sensors}
      isReadOnly={props.isReadOnly}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box position="relative" width="100%" height="100%">
        <Box
          position="sticky"
          top={0}
          left={0}
          right={0}
          paddingTop={[1, 3]}
          paddingBottom={[1, 0]}
          minHeight={[10, 15]}
          zIndex="BIG_CALENDAR_HEADER"
          backgroundColor="palettes.neutral.10.100"
        >
          <Header
            data-testid={suffixTestId('bigCalendar', props)}
            view={view}
            allowedViews={props.allowedViews || ['week', 'month', 'day']}
            currentDate={props.currentDate}
            currentUser={props.currentUser}
            selectedUsers={props.selectedUsers}
            users={props.users}
            onViewChange={setView}
            onNavigate={props.onNavigate}
            onUserControlChange={props.onUserControlChange}
          />
        </Box>
        <Show when={view === 'month'}>
          <MonthView
            data-testid={suffixTestId('bigCalendar', props)}
            currentDate={props.currentDate}
            events={optimisticEvents}
            isReadOnly={props.isReadOnly}
            onEventClick={props.onEventClick}
            onDateClick={props.onDateClick}
            enableHover={props.enableHover}
            onEventMouseEnter={handleEventMouseEnter}
            onEventMouseLeave={handleEventMouseLeave}
          />
        </Show>
        <Show when={view === 'week'}>
          <WeekView
            data-testid={suffixTestId('bigCalendar', props)}
            currentDate={props.currentDate}
            events={optimisticEvents}
            isReadOnly={props.isReadOnly}
            onEventClick={props.onEventClick}
            onDateClick={props.onDateClick}
            enableHover={props.enableHover}
            onEventMouseEnter={handleEventMouseEnter}
            onEventMouseLeave={handleEventMouseLeave}
          />
        </Show>
        <Show when={view === 'day'}>
          <DayView
            data-testid={suffixTestId('bigCalendar', props)}
            currentDate={props.currentDate}
            events={optimisticEvents}
            isReadOnly={props.isReadOnly}
            onEventClick={props.onEventClick}
            onDateClick={props.onDateClick}
            enableHover={props.enableHover}
            onEventMouseEnter={handleEventMouseEnter}
            onEventMouseLeave={handleEventMouseLeave}
          />
        </Show>

        {/* Custom Event Popup */}
        {props.renderEventPopup &&
          hoveredEvent &&
          props.renderEventPopup({
            event: hoveredEvent,
            anchorElement,
            isVisible: true,
          })}
      </Box>
    </BigCalendarDndContext>
  );
}
